import { gql, useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { Frequency, type WorkflowCompletion } from '@generated/graphql';
import { Link } from '@ovComponents/1-primative/link/link';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { DepositTransferContext } from '@ovComponents/4-module/depositWorkflow/depositTransferContext';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { PageObjectType } from '@ovComponents/5-page/pageConfiguration/pageConfigurationTypes';
import { UserContext } from '@providers/userContextProvider';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { type FieldOptions, type FormErrors, validateFields } from '../utils';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';

const FETCH_USER_FOR_PERSONA = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        physicalAddress {
          province
        }
        iDVerified
        iDCheckCompleted
      }
    }
  }
`;

export const DepositType = ({
  workflowCompletion,
  options,
  onNext,
  direction,
  dummyDeposit,
  setDummyDeposit,
  stepLoading,
}: {
  workflowCompletion?: WorkflowCompletion;
  options: any;
  onNext: any;
  direction: 'FORWARD' | 'BACKWARD';
  dummyDeposit?: any;
  setDummyDeposit?: any;
  stepLoading?: boolean;
}) => {
  const { t } = useTranslation('client');
  const { transferData } = useContext(DepositTransferContext);
  const {
    applicableData,
    updateContextWithFields,
    saveTemporaryDeposit,
    mode,
  } = useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);
  const { activeEntity, userContext } = useContext(UserContext);
  const [errors, setErrors] = useState<FormErrors>(null);
  const [focused, setFocused] = useState<string[]>([]);
  const theme = useTheme();
  const fieldOptions: FieldOptions = useMemo(
    () => ({
      type: { required: true },
    }),
    [],
  );

  const { data, loading } = useQuery(FETCH_USER_FOR_PERSONA, {
    skip: !activeEntity?.id,
    variables: { userId: activeEntity?.id },
    fetchPolicy: 'no-cache',
  });

  const showDepositBlockedBannerButton = () => {
    if (loading || !data?.fetchUser) return false;
    if (!options.enableFlowWithoutIdVerification) {
      return data.fetchUser?.user?.iDVerified === false;
    }
    return false;
  };

  const resetEFTFields = useCallback(async () => {
    await updateContextWithFields({
      schedule: Frequency.OneTime,
      amountCents: 0,
      bankAccount: {} as any,
      subAccount: {},
    });
  }, [updateContextWithFields]);

  const resetInternalTransferFields = useCallback(async () => {
    await updateContextWithFields({
      fromSubAccount: {},
      amountCents: 0,
      subAccount:
        applicableData?.objectType === PageObjectType.SUB_ACCOUNT
          ? applicableData.subAccount
          : {},
    });
  }, [
    updateContextWithFields,
    applicableData?.objectType,
    applicableData?.subAccount,
  ]);

  const resetExternalTransferFields = useCallback(async () => {
    await updateContextWithFields({
      amountCents: 0,
      institution: {},
      subAccount: {},
      accountNumber: '',
      accountType: undefined,
      transferType: 'ALL_IN_CASH',
    });
  }, [updateContextWithFields]);

  useEffect(() => {
    if (direction === 'BACKWARD') {
      switch (transferData.type) {
        case 'EFT':
          resetEFTFields();
          break;
        case 'INTERNAL_TRANSFER':
          resetInternalTransferFields();
          break;
        case 'EXTERNAL_TRANSFER':
          resetExternalTransferFields();
          break;
      }
    }
  }, [direction]);

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const validateData = {
        type: applicableData.type,
      };
      const newErrors = validateFields(
        fieldOptions,
        validateData,
        candidateFields,
      );
      setErrors(newErrors);
      return newErrors;
    },
    [fieldOptions, applicableData],
  );

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  const submit = async () => {
    const formErrors = validate();

    if (formErrors) {
      setFocused(Object.keys(formErrors));
    }

    if (
      applicableData?.type &&
      !formErrors &&
      !showDepositBlockedBannerButton()
    ) {
      await saveTemporaryDeposit();
      await onNext();
    }
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='deposit-type-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: loading || stepLoading,
        dataTestId: 'deposit-form-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackend(options?.subtitle)}
          </ReactMarkdown>
          {additionalInfo(options?.subtitle?.additionalInfo) && (
            <InfoDialog
              information={
                additionalInfo(options?.subtitle?.additionalInfo) ?? ''
              }
            />
          )}
        </Typography>
      )}
      <SelectionTile
        testId='deposit-type'
        onChange={async (e: any) => {
          if (mode === 'edit') {
            return;
          }
          await updateContextWithFields({ type: e.target.value });
        }}
        value={applicableData?.type}
        disabled={mode === 'edit'}
        options={options?.options
          ?.filter((x: any) => x.enabled)
          ?.map((option: any) => ({
            value: option.value,
            label: translateBackend(option.label),
            subtitle: translateBackend(option.description),
          }))}
        error={errors?.type?.active}
      />
      {showDepositBlockedBannerButton() && (
        <Box
          sx={{
            mt: 2,
            bgcolor: theme.color.negativeVariant,
            padding: 2,
            marginRight: 2,
            borderRadius: theme.borderRadius.md,
          }}
        >
          <Typography
            variant='bodySmall'
            component='div'
            sx={{ color: theme.color.negativeOutline, textAlign: 'justify' }}
          >
            {t('transfer:verifyIdentity.1')}
            <Link
              target='_blank'
              sx={{
                color: theme.color.negativeOutline,
                textDecorationColor: theme.color.negativeOutline,
              }}
              href={
                translateBackend(
                  userContext.organization?.translatedSupportUrl,
                ) || 'https://onevest.zendesk.com/hc/en-us/requests/new'
              }
            >
              {t('transfer:verifyIdentity.2')}
            </Link>
            {t('transfer:verifyIdentity.3')}
          </Typography>
        </Box>
      )}
    </WorkflowCompletionForm>
  );
};

export default DepositType;
